import React, { useEffect, useState } from "react";
import api from "../../../api/api";
import Swal from "sweetalert2";

import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  MenuItem,
} from "@mui/material";

import LocalShippingIcon from "@mui/icons-material/LocalShipping";

const CreateShipment = () => {
  const [loading, setLoading] = useState(false);
  const [tripLoading, setTripLoading] = useState(true);
  const [trips, setTrips] = useState([]);

  const [form, setForm] = useState({
    trip: "",
    customer_name: "",
    email: "",
    items_name: "",
    goods_image: null,
    amount_charge: "",
    discount: "",
    receiver_name: "",
    receiver_contact: "",
    receiver_address: "",
    receiver_email: "",
  });

  // FETCH TRIPS
  useEffect(() => {
    const fetchTrips = async () => {
      try {
        setTripLoading(true);
        const res = await api.get("/shipping-trips/");

        const tripsData = Array.isArray(res.data)
          ? res.data
          : res.data.results || [];

        setTrips(tripsData);
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: "Failed to load shipping trips.",
        });
        setTrips([]);
      } finally {
        setTripLoading(false);
      }
    };

    fetchTrips();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
  setForm((prev) => ({
    ...prev,
    goods_image: e.target.files[0],
  }));
};

const handleSubmit = async (e) => {
  e.preventDefault();

  if (!form.trip) {
    Swal.fire({
      icon: "warning",
      title: "Trip Required",
      text: "Please select a shipping trip.",
    });
    return;
  }

  setLoading(true);

  try {
    const formData = new FormData();

    formData.append("trip", Number(form.trip));
    formData.append("customer_name", form.customer_name);
    formData.append("email", form.email);
    formData.append("items_name", form.items_name);

    if (form.goods_image) {
      formData.append("goods_image", form.goods_image);
    }

    formData.append("amount_charge", form.amount_charge);
    formData.append("discount", form.discount || 0);
    formData.append("receiver_name", form.receiver_name);
    formData.append("receiver_contact", form.receiver_contact);
    formData.append("receiver_address", form.receiver_address);
    formData.append("receiver_email", form.receiver_email);

    await api.post("/good-shipments/", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    Swal.fire({
      icon: "success",
      title: "Shipment Created",
      text: "Tracking code generated successfully.",
      confirmButtonColor: "#f59e0b",
    });

    setForm({
      trip: "",
      customer_name: "",
      email: "",
      items_name: "",
      goods_image: null,
      amount_charge: "",
      discount: "",
      receiver_name: "",
      receiver_contact: "",
      receiver_address: "",
      receiver_email: "",
    });
  } catch (error) {
    Swal.fire({
      icon: "error",
      title: "Failed",
      text:
        error.response?.data
          ? JSON.stringify(error.response.data)
          : "Something went wrong.",
    });
  } finally {
    setLoading(false);
  }
};

  return (
    <Box
      sx={{
        width: "100%",
        background: "transparent",
        p: 1,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            background:
              "linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)",
            color: "#fff",
            p: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontWeight: "bold",
            }}
          >
            <LocalShippingIcon />
            Create Shipment
          </Typography>

        </Box>

        {/* FORM */}
        <Box sx={{ p: 2 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={1.2}>

              {/* TRIP */}
              <Grid item xs={12}>
                <TextField
                  size="small"
                  select
                  fullWidth
                  required
                  label="Shipping Trip"
                  name="trip"
                  value={form.trip}
                  onChange={handleChange}
                  disabled={tripLoading}
                >
                  {tripLoading ? (
                    <MenuItem disabled>Loading...</MenuItem>
                  ) : (
                    trips.map((trip) => (
                      <MenuItem key={trip.id} value={trip.id}>
                        {trip.name} — {trip.vessel_name}
                      </MenuItem>
                    ))
                  )}
                </TextField>
              </Grid>

              {/* CUSTOMER + EMAIL */}
              <Grid item xs={6}>
                <TextField
                  size="small"
                  fullWidth
                  required
                  label="Customer Name"
                  name="customer_name"
                  value={form.customer_name}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  size="small"
                  fullWidth
                  required
                  label="Email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                />
              </Grid>

              {/* ITEMS */}
              <Grid item xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  required
                  multiline
                  rows={2}
                  label="Items Description"
                  name="items_name"
                  value={form.items_name}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12}>
  <Button
    variant="outlined"
    component="label"
    fullWidth
    size="small"
    sx={{
      height: 40,
      justifyContent: "flex-start",
      textTransform: "none",
    }}
  >
    {form.goods_image
      ? `Selected: ${form.goods_image.name}`
      : "Upload Goods Image"}

    <input
      hidden
      type="file"
      accept="image/*"
      onChange={handleImageChange}
    />
  </Button>
</Grid>

              {/* AMOUNT + DISCOUNT */}
              <Grid item xs={6}>
                <TextField
                  size="small"
                  fullWidth
                  required
                  type="number"
                  label="Amount"
                  name="amount_charge"
                  value={form.amount_charge}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  label="Discount"
                  name="discount"
                  value={form.discount}
                  onChange={handleChange}
                />
              </Grid>

              {/* RECEIVER */}
              <Grid item xs={6}>
                <TextField
                  size="small"
                  fullWidth
                  required
                  label="Receiver Name"
                  name="receiver_name"
                  value={form.receiver_name}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  size="small"
                  fullWidth
                  required
                  label="Contact"
                  name="receiver_contact"
                  value={form.receiver_contact}
                  onChange={handleChange}
                />
              </Grid>

              {/* ADDRESS */}
              <Grid item xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  required
                  multiline
                  rows={2}
                  label="Receiver Address"
                  name="receiver_address"
                  value={form.receiver_address}
                  onChange={handleChange}
                />
              </Grid>

              {/* OPTIONAL EMAIL */}
              <Grid item xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  label="Receiver Email (Optional)"
                  name="receiver_email"
                  value={form.receiver_email}
                  onChange={handleChange}
                />
              </Grid>

              {/* SUBMIT */}
              <Grid item xs={12}>
                <Button
                  type="submit"
                  fullWidth
                  size="small"
                  disabled={loading}
                  sx={{
                    py: 1,
                    borderRadius: 2,
                    fontWeight: "bold",
                    background:
                      "linear-gradient(90deg, #f59e0b, #ea580c)",
                    color: "#fff",
                  }}
                >
                  {loading ? (
                    <CircularProgress size={18} sx={{ color: "#fff" }} />
                  ) : (
                    "Create Shipment"
                  )}
                </Button>
              </Grid>

            </Grid>
          </form>
        </Box>
      </Paper>
    </Box>
  );
};

export default CreateShipment;
